import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginFormData } from '../types';
import AuthShell from '../components/auth/AuthShell';
import AuthFormLayout from '../components/auth/AuthFormLayout';
import AuthErrorBanner from '../components/auth/AuthErrorBanner';

const loginFormSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

const LoginPage: React.FC = () => {
  const { login, error: authError, isLoggingIn, clearError } = useAuth();
  const [validationBanner, setValidationBanner] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
  });

  const dismissBanners = useCallback(() => {
    clearError();
    setValidationBanner(null);
  }, [clearError]);

  const onSubmit = async (data: LoginFormData) => {
    setValidationBanner(null);
    await login(data);
  };

  const onInvalid = () => {
    clearError();
    setValidationBanner('Please enter your username and password.');
  };

  const bannerMessage = authError ?? validationBanner;

  return (
    <AuthShell>
      {bannerMessage && (
        <AuthErrorBanner message={bannerMessage} onDismiss={dismissBanners} />
      )}
      <AuthFormLayout
        heading="Welcome back"
        subtext="Sign in to Eventus Administration"
      >
        <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
          <div>
            <input
              id="username"
              type="text"
              autoComplete="username"
              className="auth-input"
              placeholder="Username"
              {...register('username')}
            />
            {errors.username && (
              <p className="auth-error">{errors.username.message}</p>
            )}
          </div>

          <div>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              className="auth-input"
              placeholder="Password"
              {...register('password')}
            />
            {errors.password && (
              <p className="auth-error">{errors.password.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoggingIn}
            className="auth-btn-primary"
            aria-busy={isLoggingIn}
          >
            {isLoggingIn ? (
              <span className="flex items-center justify-center gap-2">
                <span className="auth-btn-spinner" aria-hidden="true" />
                Signing in…
              </span>
            ) : (
              'Sign in'
            )}
          </button>

          <div className="pt-3 text-center">
            <Link to="/forgot-password" className="auth-link text-sm">
              Forgot password?
            </Link>
          </div>
        </form>
      </AuthFormLayout>
    </AuthShell>
  );
};

export default LoginPage;
